import { Alert, AlertIcon, Button, Flex, FormControl, HStack, Heading, Input } from "@chakra-ui/react";
import React, { FunctionComponent, ReactElement, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Components
import ClaimHyperlink from "../../shared/DetailElement/Elements/ClaimHyperlink";
import { MainPageCard } from "../../shared/layout/index.styles";

// Images
import logo from "../../assets/images/logo.png";

// Utils
import { RouterPathMapping } from "../../Router";

// Hooks
import { useAuth } from "../../hooks/useAuth";
import { useAzureLogin } from "../../hooks/useAzureLogin";
import useClaimsToast from "../../hooks/useToast";

// SSO Utils
import { isSSODomain } from "../../utils/ssoConfig";

interface LoginPageProps {}

const LoginPage: FunctionComponent<LoginPageProps> = (props: LoginPageProps): ReactElement => {
	// State
	const [email, setEmail] = useState<string>("");
	const [password, setPassword] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showSessionExpired, setShowSessionExpired] = useState<boolean>(false);
	const [showPasswordField, setShowPasswordField] = useState<boolean>(false);

	// Hooks
	const navigate = useNavigate();
	const { loginInternal, startSSOLogin, consumeStoredDestination, isAuthInteractionInProgress, isSSOLoginInProgress } =
		useAuth();
	const toast = useClaimsToast();

	// Always call useAzureLogin at the top level
	useAzureLogin();

	// Track if we've already shown the session expired toast
	const hasShownSessionExpiredToast = useRef(false);

	// Check for session expiration flag and show toast
	useEffect(() => {
		const sessionExpired = localStorage.getItem("sessionExpired");

		if (sessionExpired === "true" && !hasShownSessionExpiredToast.current) {
			hasShownSessionExpiredToast.current = true;
			setShowSessionExpired(true);
			// Clear the flag first to prevent duplicate processing
			localStorage.removeItem("sessionExpired");
			// Show toast after a brief delay to ensure the page is fully rendered
			setTimeout(() => {
				toast("Error", "Your session has expired. Please log in again.", "error");
			}, 100);
		}
	}, [toast]);

	/**
	 * Handles updating email/password state variables
	 * @param event Input Changed Event
	 * @param delegate The React State Update function
	 */
	const handleFieldUpdate = (
		event: React.ChangeEvent<HTMLInputElement>,
		delegate: React.Dispatch<React.SetStateAction<string>>
	): void => {
		delegate(event.target.value);
	};

	/**
	 * Handles email/username submission - checks if SSO domain and routes accordingly
	 */
	const handleEmailSubmit = (): void => {
		if (!email) {
			toast(undefined, "Please enter your username or email", "error");
			return;
		}

		// Check if it's an email with SSO domain
		if (email.includes("@") && isSSODomain(email)) {
			// SSO domain - redirect to Microsoft
			handleSSOLogin();
		} else {
			// Username or regular domain - show password field
			setShowPasswordField(true);
		}
	};

	/**
	 * Handles submitting the login form when the Enter key is pressed
	 * @param event The Key Down Event
	 * @param isPasswordField Whether this is the password field
	 */
	const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, isPasswordField: boolean = false): void => {
		if (event.key.toLowerCase() === "enter") {
			if (isPasswordField) {
				handleLogin();
			} else {
				handleEmailSubmit();
			}
		}
	};

	/**
	 * Handles SSO login by redirecting to Microsoft Azure AD
	 */
	const handleSSOLogin = (): void => {
		startSSOLogin(email).catch((error) => {
			console.error("Login redirect failed:", error);
			toast(undefined, "Microsoft sign-in failed. Please try again.", "error");
		});
	};

	/**
	 * Handles the login process. Will catch errors and display error toast messages
	 */
	const handleLogin = async (): Promise<void> => {
		setIsLoading(true);
		try {
			const response = await loginInternal(email, password);
			navigate(consumeStoredDestination(response.getUser()?.getRole()), { replace: true });
		} catch (err) {
			console.error("Internal login failed: " + err);
			toast(undefined, "Username or password is incorrect, please try again", "error");
		}
		setIsLoading(false);
	};

	return (
		<Flex flexDirection={"column"} justifyContent="center" alignItems={"center"} p={7} w="100%">
			<img src={logo} alt={"logo"} />
			<MainPageCard mt={6}>
				<Heading mb={"5"} textAlign="center" color="secondary">
					Sign In
				</Heading>
				{showSessionExpired && (
					<Alert status="warning" mb={4}>
						<AlertIcon />
						Your session has expired. Please log in again.
					</Alert>
				)}
				<Flex flexDirection={"column"} alignItems="center">
					<FormControl maxW={400}>
						<Input
							variant="mainInput"
							type="text"
							id="email"
							data-testid="email-input"
							placeholder="Username or Email"
							value={email}
							disabled={isLoading || showPasswordField}
							onKeyDown={(e) => handleInputKeyDown(e, false)}
							onChange={(e) => handleFieldUpdate(e, setEmail)}
							autoFocus
						/>
					</FormControl>
					{showPasswordField && (
						<>
							<FormControl maxW={400} my={3}>
								<Input
									variant="mainInput"
									id="password"
									data-testid="password-input"
									type="Password"
									placeholder="Password"
									value={password}
									disabled={isLoading}
									onKeyDown={(e) => handleInputKeyDown(e, true)}
									onChange={(e) => handleFieldUpdate(e, setPassword)}
									autoFocus
								/>
							</FormControl>
							<Button
								variant="link"
								size="sm"
								colorScheme="gray"
								onClick={() => {
									setShowPasswordField(false);
									setPassword("");
								}}
								isDisabled={isLoading}>
								← Change email/username
							</Button>
						</>
					)}
					{showPasswordField ? (
						<>
							<Button
								type="submit"
								data-testid="submit-login"
								colorScheme="orange"
								minW={200}
								mt={5}
								isLoading={isLoading}
								onClick={() => handleLogin()}>
								Login
							</Button>
							<HStack mt={3}>
								<ClaimHyperlink
									isExternal={false}
									linkHref={RouterPathMapping.ForgotPassword}
									linkText="Forgot Password?"
								/>
							</HStack>
						</>
					) : (
						<Button
							type="submit"
							data-testid="submit-email"
							colorScheme="orange"
							minW={200}
							mt={5}
							isLoading={isSSOLoginInProgress}
							isDisabled={!email || isAuthInteractionInProgress}
							onClick={handleEmailSubmit}>
							Continue
						</Button>
					)}
				</Flex>
			</MainPageCard>
		</Flex>
	);
};

export default LoginPage;
